import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePermissions } from '../../hooks/usePermissions'
import { citaService } from '../../services/citaService'
import { mascotaService } from '../../services/mascotaService'
import { clinicaService } from '../../services/clinicaService'
import { suscripcionClinicaService } from '../../services/suscripcionClinicaService'
import { Calendar, ChevronLeft, ChevronRight, Plus, PawPrint, Clock, CheckCircle2, XCircle, CalendarCheck2 } from 'lucide-react'
import LoadingState from '../../components/ui/LoadingState'
import EmptyState from '../../components/ui/EmptyState'
import {
  TIPO_CITA_OPTIONS,
  DEFAULT_TIPO_CITA,
  getTipoCitaLabel,
  isTipoCitaClinicaRequired
} from '../../constants/tipoCita'

function extractArray(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

function parseDateValue(value) {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'number') {
    const dateFromNumber = new Date(value)
    return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber
  }

  const raw = String(value).trim()
  if (!raw) return null

  // Evita corrimiento de zona horaria con fechas "YYYY-MM-DD".
  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnlyMatch) {
    const [, y, m, d] = dateOnlyMatch
    return new Date(Number(y), Number(m) - 1, Number(d))
  }

  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function toDateKey(value) {
  const date = parseDateValue(value)
  if (!date) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function monthLabel(date) {
  return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
}

function getCitaDateKey(cita = {}) {
  return toDateKey(
    cita.fecha ||
      cita.fecha_cita ||
      cita.fechaCita ||
      cita.fecha_cita_confirmada ||
      cita.fechaCitaConfirmada ||
      cita.fecha_cita_propuesta ||
      cita.fechaCitaPropuesta
  )
}

function toTimeSortValue(value) {
  if (!value) return Number.MAX_SAFE_INTEGER
  const normalized = String(value).trim()
  const match = normalized.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return Number.MAX_SAFE_INTEGER
  const [, h, m] = match
  return Number(h) * 60 + Number(m)
}

function normalizeEstado(estado) {
  return String(estado || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
}

function getCitaId(cita = {}) {
  return cita.id_cita || cita.idCita || cita.id
}

function getCitaHour(cita = {}) {
  return (
    cita.hora_cita ||
    cita.horaCita ||
    cita.hora_cita_confirmada ||
    cita.horaCitaConfirmada ||
    cita.hora_cita_propuesta ||
    cita.horaCitaPropuesta ||
    '--:--'
  )
}

function getCitaMascotaName(cita = {}) {
  return cita.mascota?.nombre || cita.nombre_mascota || cita.nombreMascota || 'Mascota'
}

function isPendingApprovalState(estado = '') {
  const value = normalizeEstado(estado)
  return value.includes('PENDIENTE') && (value.includes('APROB') || value.includes('AUTORIZ'))
}

function isApprovedState(estado = '') {
  const value = normalizeEstado(estado)
  return value.includes('APROBAD') || value === 'APROBADA' || value === 'APROBADO'
}

function isConfirmedState(estado = '') {
  const value = normalizeEstado(estado)
  return value.includes('CONFIRM')
}

function isPetSitterRequest(cita = {}) {
  const raw =
    cita.tipo_solicitante ||
    cita.tipoSolicitante ||
    cita.solicitante_tipo ||
    cita.solicitanteTipo ||
    ''
  return String(raw).trim().toUpperCase().replace(/[\s-]+/g, '_').includes('PET_SITTER')
}

export default function CitasMobile() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    isDueno,
    isPetSitter,
    isVeterinario,
    isAdmin,
    userClinicaId,
    canAprobarCita,
    canRechazarCita,
    canAsignarFechaCita,
    canCompletarCita
  } = usePermissions()

  /** Dueño / pet sitter / admin deben elegir clínica; veterinario usa la clínica del perfil */
  const requiresClinicaSelector = isDueno || isPetSitter || isAdmin

  const [citas, setCitas] = useState([])
  const [mascotas, setMascotas] = useState([])
  const [clinicas, setClinicas] = useState([])
  const [clinicasSuscritas, setClinicasSuscritas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [showActionModal, setShowActionModal] = useState(false)
  const [actionType, setActionType] = useState('')
  const [selectedCita, setSelectedCita] = useState(null)
  const [processingAction, setProcessingAction] = useState(false)
  const [actionError, setActionError] = useState('')
  const [assignData, setAssignData] = useState({ fecha_cita_propuesta: '', hora_cita_propuesta: '' })
  const [rejectMotivo, setRejectMotivo] = useState('')
  const [completeData, setCompleteData] = useState({ observaciones: '' })
  const [monthCursor, setMonthCursor] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })
  const [selectedDayKey, setSelectedDayKey] = useState(toDateKey(new Date()))
  const [formData, setFormData] = useState({
    id_mascota: '',
    id_clinica: '',
    tipo_cita: DEFAULT_TIPO_CITA,
    descripcion_cita: '',
    fecha_cita_propuesta: toDateKey(new Date()),
    hora_cita_propuesta: '09:00'
  })

  const clinicasParaSeleccion = useMemo(() => {
    if (isDueno || isPetSitter) {
      return clinicasSuscritas.length > 0 ? clinicasSuscritas : clinicas
    }
    return clinicas
  }, [isDueno, isPetSitter, clinicasSuscritas, clinicas])

  const fetchClinicasContext = useCallback(async () => {
    try {
      if (isAdmin) {
        const res = await clinicaService.getAll({ per_page: 100 })
        if (res.success) {
          setClinicas(res.data.data || res.data || [])
        }
      }
      if (isDueno || isPetSitter) {
        const [subRes, allRes] = await Promise.all([
          suscripcionClinicaService.getAll(),
          clinicaService.getAll({ per_page: 100 })
        ])
        if (allRes.success) {
          setClinicas(allRes.data.data || allRes.data || [])
        }
        if (subRes.success) {
          const suscripciones = subRes.data.data || subRes.data || []
          const clinics = suscripciones
            .map((sub) => sub?.clinica)
            .filter(Boolean)
            .filter((clinica, index, arr) => {
              const clinicaId = clinica.id_clinica || clinica.idClinica || clinica.id
              return arr.findIndex((item) => (item.id_clinica || item.idClinica || item.id) === clinicaId) === index
            })
          setClinicasSuscritas(clinics)
        }
      }
    } catch (err) {
      console.error('Error cargando clínicas para citas:', err)
    }
  }, [isAdmin, isDueno, isPetSitter])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError('')
      const citasPromise = isDueno ? citaService.getCitasByDueno() : citaService.getAll()
      const mascotasPromise = isDueno ? mascotaService.getMascotasByDueno() : mascotaService.getAll({ per_page: 100 })
      const [citasRes, mascotasRes] = await Promise.all([citasPromise, mascotasPromise])

      const citasData = extractArray(citasRes)
      const mascotasData = extractArray(mascotasRes)
      setCitas(citasData)
      setMascotas(mascotasData)
      if (!formData.id_mascota && mascotasData.length > 0) {
        setFormData((prev) => ({
          ...prev,
          id_mascota: String(mascotasData[0].id_mascota || mascotasData[0].idMascota || '')
        }))
      }
      await fetchClinicasContext()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudieron cargar las citas')
      setCitas([])
      setMascotas([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDueno, isPetSitter, isAdmin])

  const openCreateModal = useCallback(async () => {
    setError('')
    setShowCreateModal(true)
    await fetchClinicasContext()
    if (isDueno || isPetSitter) {
      try {
        const response = await suscripcionClinicaService.getAll()
        if (response.success) {
          const suscripciones = response.data.data || response.data || []
          const suscripcionActiva = suscripciones.find((sub) => sub.estado === 'ACTIVA')
          if (suscripcionActiva?.id_clinica) {
            setFormData((prev) => ({
              ...prev,
              id_clinica: String(suscripcionActiva.id_clinica)
            }))
          }
        }
      } catch (err) {
        console.error('Error al preseleccionar clínica:', err)
      }
    }
  }, [fetchClinicasContext, isDueno, isPetSitter])

  useEffect(() => {
    const openCreate = searchParams.get('nuevo') === '1'
    const preselectedMascota = searchParams.get('mascota')
    if (preselectedMascota) {
      setFormData((prev) => ({ ...prev, id_mascota: String(preselectedMascota) }))
    }
    if (openCreate) {
      void openCreateModal()
      const next = new URLSearchParams(searchParams)
      next.delete('nuevo')
      next.delete('mascota')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams, openCreateModal])

  const citasPorDia = useMemo(() => {
    const map = new Map()
    citas.forEach((cita) => {
      const key = getCitaDateKey(cita)
      if (!key) return
      const list = map.get(key) || []
      list.push(cita)
      map.set(key, list)
    })
    return map
  }, [citas])

  const selectedEvents = useMemo(() => {
    const list = [...(citasPorDia.get(selectedDayKey) || [])]
    list.sort((a, b) => {
      const horaA = toTimeSortValue(
        a.hora_cita ||
          a.horaCita ||
          a.hora_cita_confirmada ||
          a.horaCitaConfirmada ||
          a.hora_cita_propuesta ||
          a.horaCitaPropuesta
      )
      const horaB = toTimeSortValue(
        b.hora_cita ||
          b.horaCita ||
          b.hora_cita_confirmada ||
          b.horaCitaConfirmada ||
          b.hora_cita_propuesta ||
          b.horaCitaPropuesta
      )
      return horaA - horaB
    })
    return list
  }, [citasPorDia, selectedDayKey])

  const calendarCells = useMemo(() => {
    const year = monthCursor.getFullYear()
    const month = monthCursor.getMonth()
    const first = new Date(year, month, 1)
    const last = new Date(year, month + 1, 0)
    const total = last.getDate()
    const offset = (first.getDay() + 6) % 7 // Monday first
    const cells = []

    for (let i = 0; i < offset; i += 1) {
      cells.push({ type: 'empty', key: `e-${i}` })
    }
    for (let day = 1; day <= total; day += 1) {
      const date = new Date(year, month, day)
      const key = toDateKey(date)
      cells.push({ type: 'day', day, key, hasEvents: (citasPorDia.get(key) || []).length > 0 })
    }
    return cells
  }, [monthCursor, citasPorDia])

  useEffect(() => {
    const currentHasEvents = (citasPorDia.get(selectedDayKey) || []).length > 0
    if (currentHasEvents) return

    const monthPrefix = `${monthCursor.getFullYear()}-${String(monthCursor.getMonth() + 1).padStart(2, '0')}`
    const monthKeys = [...citasPorDia.keys()]
      .filter((key) => key.startsWith(`${monthPrefix}-`))
      .sort()

    if (monthKeys.length > 0) {
      setSelectedDayKey(monthKeys[0])
    }
  }, [citasPorDia, monthCursor, selectedDayKey])

  const handleCreateCita = async (event) => {
    event.preventDefault()
    if (!formData.id_mascota || !formData.fecha_cita_propuesta || !formData.hora_cita_propuesta) {
      setError('Completa mascota, fecha y hora para solicitar la cita')
      return
    }

    const clinicaObligatoria = isTipoCitaClinicaRequired(formData.tipo_cita)
    let idClinica = null

    if (isVeterinario) {
      if (clinicaObligatoria) {
        const cid = Number(userClinicaId)
        if (userClinicaId == null || Number.isNaN(cid) || cid <= 0) {
          setError('No tienes una clínica asignada. No se puede crear este tipo de cita.')
          return
        }
        idClinica = cid
      }
    } else if (requiresClinicaSelector) {
      if (clinicaObligatoria) {
        if (clinicasParaSeleccion.length === 0) {
          setError(
            isDueno || isPetSitter
              ? 'No tienes clínicas disponibles. Suscríbete a una clínica antes de solicitar la cita.'
              : 'No hay clínicas disponibles para seleccionar.'
          )
          return
        }
        if (!formData.id_clinica) {
          setError('Selecciona una clínica')
          return
        }
        idClinica = Number(formData.id_clinica)
      } else if (formData.id_clinica) {
        idClinica = Number(formData.id_clinica)
      }
    } else {
      setError('Tu rol no permite crear citas desde aquí o falta configuración de clínica.')
      return
    }

    try {
      setCreating(true)
      setError('')
      const payload = {
        id_mascota: Number(formData.id_mascota),
        tipo_cita: formData.tipo_cita,
        descripcion_cita: formData.descripcion_cita,
        fecha_cita_propuesta: formData.fecha_cita_propuesta,
        hora_cita_propuesta: formData.hora_cita_propuesta
      }
      if (idClinica != null) {
        payload.id_clinica = idClinica
      }
      const response = await citaService.create(payload)
      if (!response?.success) {
        throw new Error(response?.error || 'No se pudo crear la cita')
      }
      setShowCreateModal(false)
      setFormData((prev) => ({ ...prev, id_clinica: '' }))
      await fetchData()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudo crear la cita')
    } finally {
      setCreating(false)
    }
  }

  const closeActionModal = () => {
    setShowActionModal(false)
    setActionType('')
    setSelectedCita(null)
    setActionError('')
    setAssignData({ fecha_cita_propuesta: '', hora_cita_propuesta: '' })
    setRejectMotivo('')
    setCompleteData({ observaciones: '' })
  }

  const openActionModal = (type, cita) => {
    setActionError('')
    setSelectedCita(cita)
    setActionType(type)
    if (type === 'asignar') {
      const defaultDate =
        cita.fecha_cita_propuesta ||
        cita.fechaCitaPropuesta ||
        cita.fecha_cita_confirmada ||
        cita.fechaCitaConfirmada ||
        toDateKey(new Date())
      const defaultHour = getCitaHour(cita) === '--:--' ? '09:00' : getCitaHour(cita)
      setAssignData({
        fecha_cita_propuesta: toDateKey(defaultDate) || toDateKey(new Date()),
        hora_cita_propuesta: defaultHour
      })
    }
    setShowActionModal(true)
  }

  const handleQuickApprove = async (cita) => {
    const citaId = getCitaId(cita)
    if (!citaId) return
    try {
      setProcessingAction(true)
      setError('')
      const response = await citaService.aprobar(citaId)
      if (!response?.success) {
        throw new Error(response?.error || 'No se pudo aprobar la cita')
      }
      openActionModal('asignar', cita)
      await fetchData()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudo aprobar la cita')
    } finally {
      setProcessingAction(false)
    }
  }

  const handleReject = async () => {
    const citaId = getCitaId(selectedCita)
    if (!citaId) return
    if (!rejectMotivo.trim() || rejectMotivo.trim().length < 10) {
      setActionError('El motivo de rechazo debe tener al menos 10 caracteres')
      return
    }
    try {
      setProcessingAction(true)
      setActionError('')
      const response = await citaService.rechazar(citaId, rejectMotivo.trim())
      if (!response?.success) {
        throw new Error(response?.error || response?.message || 'No se pudo rechazar la cita')
      }
      closeActionModal()
      await fetchData()
    } catch (err) {
      setActionError(err.response?.data?.error || err.response?.data?.message || err.message || 'No se pudo rechazar la cita')
    } finally {
      setProcessingAction(false)
    }
  }

  const handleAssignDateTime = async () => {
    const citaId = getCitaId(selectedCita)
    if (!citaId) return
    if (!assignData.fecha_cita_propuesta || !assignData.hora_cita_propuesta) {
      setActionError('Debes ingresar fecha y hora')
      return
    }
    try {
      setProcessingAction(true)
      setActionError('')
      const response = await citaService.asignarFechaHora(citaId, assignData)
      if (!response?.success) {
        throw new Error(response?.error || 'No se pudo asignar fecha y hora')
      }
      closeActionModal()
      await fetchData()
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'No se pudo asignar fecha y hora')
    } finally {
      setProcessingAction(false)
    }
  }

  const handleComplete = async () => {
    const citaId = getCitaId(selectedCita)
    if (!citaId) return
    try {
      setProcessingAction(true)
      setActionError('')
      const payload = {
        observaciones: completeData.observaciones || 'Completada desde calendario móvil'
      }
      const response = await citaService.completar(citaId, payload)
      if (!response?.success) {
        throw new Error(response?.error || 'No se pudo completar la cita')
      }
      closeActionModal()
      await fetchData()
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'No se pudo completar la cita')
    } finally {
      setProcessingAction(false)
    }
  }

  if (loading) {
    return <LoadingState message="Cargando calendario..." />
  }

  return (
    <div className="min-h-full bg-white pb-20">
      <div className="px-4 pt-3 pb-1 flex items-center justify-between">
        <button
          onClick={() => navigate('/')}
          type="button"
          className="h-8 w-8 rounded-full bg-[#f0f2f5] text-[#666] flex items-center justify-center"
          aria-label="Ir a inicio"
        >
          <Calendar className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonthCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
            type="button"
            className="h-7 w-7 rounded-full bg-[#f4f4f4] text-[#666] flex items-center justify-center"
            aria-label="Mes anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[13px] font-semibold text-[#111] capitalize min-w-[130px] text-center">{monthLabel(monthCursor)}</span>
          <button
            onClick={() => setMonthCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
            type="button"
            className="h-7 w-7 rounded-full bg-[#f4f4f4] text-[#666] flex items-center justify-center"
            aria-label="Mes siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={() => {
            const firstPetId = mascotas[0]?.id_mascota || mascotas[0]?.idMascota
            if (firstPetId) navigate(`/mascotas/${firstPetId}`)
            else navigate('/perfil')
          }}
          type="button"
          className="h-8 w-8 rounded-full bg-gradient-to-br from-[#d4b896] to-[#e8e0d8] border border-white shadow-sm text-sm"
          aria-label="Ir a perfil de mascota"
        >
          🐕
        </button>
      </div>

      <div className="px-4 pb-2">
        <div className="rounded-[18px] border border-[#ececec] p-3">
          <div className="grid grid-cols-7 mb-2 text-center text-[10px] text-[#999]">
            {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((w) => <span key={w}>{w}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-y-2 text-center">
            {calendarCells.map((cell) => {
              if (cell.type === 'empty') return <span key={cell.key} />
              const selected = cell.key === selectedDayKey
              return (
                <button
                  key={cell.key}
                  onClick={() => setSelectedDayKey(cell.key)}
                  type="button"
                  className={`h-9 w-9 mx-auto rounded-full text-[12px] relative ${
                    selected ? 'bg-[#7a9a52] text-white font-bold' : 'text-[#333] hover:bg-[#f5f5f5]'
                  }`}
                >
                  {cell.day}
                  {cell.hasEvents && (
                    <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full ${selected ? 'bg-white' : 'bg-[#5b87d8]'}`} />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="px-4 pb-2 flex items-center justify-between gap-2">
        <div>
          <h2 className="text-[14px] font-bold text-[#111]">Citas del día</h2>
          <p className="text-[10px] text-[#8a94a3] mt-0.5">
            {selectedDayKey || 'Sin fecha'} · {selectedEvents.length} cita{selectedEvents.length === 1 ? '' : 's'}
          </p>
        </div>
        <button
          onClick={() => void openCreateModal()}
          type="button"
          className="text-[12px] text-[#7a9a52] font-semibold inline-flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" />
          Nueva cita
        </button>
      </div>

      <div className="px-4 pb-6 space-y-2.5">
        {selectedEvents.length === 0 ? (
          <EmptyState
            icon={Clock}
            title="Sin eventos para este día"
            message="No tienes citas registradas en la fecha seleccionada."
          />
        ) : (
          selectedEvents.map((cita, idx) => {
            const citaId = getCitaId(cita)
            const colorClass = idx % 2 === 0 ? 'bg-[#5b87d8]' : 'bg-[#c9883a]'
            const hora = getCitaHour(cita)
            const estado = normalizeEstado(cita.estado)
            const pendingApproval = isPendingApprovalState(estado)
            const approved = isApprovedState(estado)
            const confirmed = isConfirmedState(estado)
            const petSitterFlow = isPetSitterRequest(cita)
            const petSitterApproval = isDueno && pendingApproval && petSitterFlow
            const canApprove =
              pendingApproval && ((canAprobarCita && !petSitterFlow) || petSitterApproval)
            const canReject =
              pendingApproval && ((canRechazarCita && !petSitterFlow) || petSitterApproval)
            const canAssign = canAsignarFechaCita && approved
            const canComplete = canCompletarCita && (confirmed || approved)
            const hasPendingAction = canApprove || canReject || canAssign || canComplete
            return (
              <div
                key={citaId || `${selectedDayKey}-${idx}`}
                role="button"
                tabIndex={citaId ? 0 : -1}
                onClick={() => {
                  if (citaId) navigate(`/citas/${citaId}`)
                }}
                onKeyDown={(event) => {
                  if (!citaId) return
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    navigate(`/citas/${citaId}`)
                  }
                }}
                className={`w-full rounded-[14px] p-3 text-left text-white ${colorClass} ${citaId ? 'cursor-pointer' : 'opacity-70'}`}
              >
                <p className="text-[12px] font-bold">{getTipoCitaLabel(cita.tipo_cita || cita.tipoCita)}</p>
                <p className="text-[11px] opacity-90 mt-0.5">
                  {hora} · {getCitaMascotaName(cita)}
                </p>
                <p className="text-[10px] opacity-90 mt-1">{cita.clinica?.nombre || 'Clínica no asignada'}</p>
                <span className="mt-2 inline-block text-[9px] font-bold bg-white/25 rounded-[8px] px-2 py-0.5">
                  {cita.estado || 'PENDIENTE'}
                </span>
                {hasPendingAction ? (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {canApprove ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          handleQuickApprove(cita)
                        }}
                        disabled={processingAction}
                        className="h-7 rounded-lg bg-white/20 px-2.5 text-[10px] font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Aprobar
                      </button>
                    ) : null}
                    {canReject ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          openActionModal('rechazar', cita)
                        }}
                        disabled={processingAction}
                        className="h-7 rounded-lg bg-black/20 px-2.5 text-[10px] font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                      >
                        <XCircle className="w-3 h-3" />
                        Rechazar
                      </button>
                    ) : null}
                    {canAssign ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          openActionModal('asignar', cita)
                        }}
                        className="h-7 rounded-lg bg-white/20 px-2.5 text-[10px] font-semibold inline-flex items-center gap-1"
                      >
                        <CalendarCheck2 className="w-3 h-3" />
                        Asignar fecha
                      </button>
                    ) : null}
                    {canComplete ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          openActionModal('completar', cita)
                        }}
                        className="h-7 rounded-lg bg-white/20 px-2.5 text-[10px] font-semibold inline-flex items-center gap-1"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Completar
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-2 text-[10px] opacity-80">Sin acciones pendientes para este estado</p>
                )}
              </div>
            )
          })
        )}
      </div>

      {showActionModal && (
        <div className="fixed inset-0 z-[85] bg-black/45 flex items-end justify-center p-3">
          <div className="w-full max-w-md rounded-[20px] bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[15px] font-bold text-[#111] capitalize">
                {actionType === 'rechazar'
                  ? 'Rechazar cita'
                  : actionType === 'asignar'
                    ? 'Asignar fecha y hora'
                    : 'Completar cita'}
              </h3>
              <button type="button" onClick={closeActionModal} className="text-[#888]">✕</button>
            </div>

            <div className="rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2">
              <p className="text-[12px] font-semibold text-[#222]">{getCitaMascotaName(selectedCita || {})}</p>
              <p className="text-[11px] text-[#666] mt-0.5">
                Estado actual: {(selectedCita?.estado || 'PENDIENTE').toString()}
              </p>
            </div>

            {selectedCita &&
              isPendingApprovalState(selectedCita.estado) &&
              (selectedCita.fecha_cita_propuesta ||
                selectedCita.fechaCitaPropuesta ||
                selectedCita.hora_cita_propuesta ||
                selectedCita.horaCitaPropuesta) && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5">
                  <p className="text-[11px] font-bold text-sky-900">Solicitud tentativa del cliente</p>
                  <p className="text-[12px] text-sky-950 mt-1">
                    {selectedCita.fecha_cita_propuesta || selectedCita.fechaCitaPropuesta
                      ? new Date(
                          selectedCita.fecha_cita_propuesta || selectedCita.fechaCitaPropuesta
                        ).toLocaleDateString('es-ES', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })
                      : 'Fecha no indicada'}
                    {selectedCita.hora_cita_propuesta || selectedCita.horaCitaPropuesta
                      ? ` · ${selectedCita.hora_cita_propuesta || selectedCita.horaCitaPropuesta}`
                      : ''}
                  </p>
                </div>
              )}

            {actionType === 'rechazar' ? (
              <label className="block">
                <span className="text-[12px] text-[#555]">Motivo de rechazo</span>
                <textarea
                  value={rejectMotivo}
                  onChange={(event) => setRejectMotivo(event.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px] resize-none"
                  placeholder="Indica un motivo claro para el usuario"
                />
              </label>
            ) : null}

            {actionType === 'asignar' ? (
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[12px] text-[#555]">Fecha</span>
                  <input
                    type="date"
                    value={assignData.fecha_cita_propuesta}
                    onChange={(event) =>
                      setAssignData((prev) => ({ ...prev, fecha_cita_propuesta: event.target.value }))
                    }
                    className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px]"
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] text-[#555]">Hora</span>
                  <input
                    type="time"
                    value={assignData.hora_cita_propuesta}
                    onChange={(event) =>
                      setAssignData((prev) => ({ ...prev, hora_cita_propuesta: event.target.value }))
                    }
                    className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px]"
                  />
                </label>
              </div>
            ) : null}

            {actionType === 'completar' ? (
              <label className="block">
                <span className="text-[12px] text-[#555]">Observaciones</span>
                <textarea
                  value={completeData.observaciones}
                  onChange={(event) => setCompleteData({ observaciones: event.target.value })}
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px] resize-none"
                  placeholder="Describe resultado, indicaciones o seguimiento"
                />
              </label>
            ) : null}

            {actionError ? <p className="text-[12px] text-[#e05555]">{actionError}</p> : null}

            <button
              type="button"
              disabled={processingAction}
              onClick={() => {
                if (actionType === 'rechazar') handleReject()
                else if (actionType === 'asignar') handleAssignDateTime()
                else handleComplete()
              }}
              className="w-full h-11 rounded-xl bg-[#7a9a52] text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {processingAction ? 'Procesando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-end justify-center p-3">
          <form onSubmit={handleCreateCita} className="w-full max-w-md rounded-[20px] bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[15px] font-bold text-[#111]">Solicitar cita</h3>
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false)
                  setFormData((prev) => ({ ...prev, id_clinica: '' }))
                }}
                className="text-[#888]"
              >
                ✕
              </button>
            </div>

            <label className="block">
              <span className="text-[12px] text-[#555]">Mascota</span>
              <select
                value={formData.id_mascota}
                onChange={(e) => setFormData((prev) => ({ ...prev, id_mascota: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px]"
              >
                <option value="">Selecciona mascota</option>
                {mascotas.map((m) => {
                  const idMascota = m.id_mascota || m.idMascota
                  return (
                    <option key={idMascota} value={idMascota}>
                      {m.nombre || `Mascota ${idMascota}`}
                    </option>
                  )
                })}
              </select>
            </label>

            <label className="block">
              <span className="text-[12px] text-[#555]">Tipo</span>
              <select
                value={formData.tipo_cita}
                onChange={(e) => setFormData((prev) => ({ ...prev, tipo_cita: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px]"
              >
                {TIPO_CITA_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            {requiresClinicaSelector && isTipoCitaClinicaRequired(formData.tipo_cita) ? (
              <label className="block">
                <span className="text-[12px] text-[#555]">
                  Clínica <span className="text-red-500">*</span>
                </span>
                <select
                  value={formData.id_clinica}
                  onChange={(e) => setFormData((prev) => ({ ...prev, id_clinica: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px]"
                  required
                >
                  <option value="">Selecciona clínica</option>
                  {clinicasParaSeleccion.map((c) => {
                    const id = c.id_clinica || c.idClinica || c.id
                    return (
                      <option key={id} value={id}>
                        {c.nombre || `Clínica ${id}`}
                      </option>
                    )
                  })}
                </select>
                {(isDueno || isPetSitter) && clinicasParaSeleccion.length === 0 ? (
                  <p className="mt-1 text-[11px] text-amber-700">
                    No tienes clínicas suscritas. Suscríbete a una clínica antes de pedir la cita.
                  </p>
                ) : null}
              </label>
            ) : requiresClinicaSelector && !isTipoCitaClinicaRequired(formData.tipo_cita) ? (
              <label className="block">
                <span className="text-[12px] text-[#555]">Clínica (opcional)</span>
                <select
                  value={formData.id_clinica}
                  onChange={(e) => setFormData((prev) => ({ ...prev, id_clinica: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px]"
                >
                  <option value="">Sin clínica</option>
                  {clinicasParaSeleccion.map((c) => {
                    const id = c.id_clinica || c.idClinica || c.id
                    return (
                      <option key={id} value={id}>
                        {c.nombre || `Clínica ${id}`}
                      </option>
                    )
                  })}
                </select>
              </label>
            ) : isVeterinario && userClinicaId && isTipoCitaClinicaRequired(formData.tipo_cita) ? (
              <div className="rounded-xl border border-[#e8f5e9] bg-[#f4faf4] px-3 py-2 text-[12px] text-[#2e5c2e]">
                Clínica asignada a tu perfil (no requiere selección).
              </div>
            ) : isVeterinario && !isTipoCitaClinicaRequired(formData.tipo_cita) ? (
              <div className="rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[12px] text-[#555]">
                Este tipo de evento no requiere clínica.
              </div>
            ) : null}

            <label className="block">
              <span className="text-[12px] text-[#555]">Descripción</span>
              <textarea
                value={formData.descripcion_cita}
                onChange={(e) => setFormData((prev) => ({ ...prev, descripcion_cita: e.target.value }))}
                rows={2}
                className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px] resize-none"
                placeholder="Motivo de la consulta"
              />
            </label>

            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[12px] text-[#555]">Fecha</span>
                <input
                  type="date"
                  value={formData.fecha_cita_propuesta}
                  onChange={(e) => setFormData((prev) => ({ ...prev, fecha_cita_propuesta: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px]"
                />
              </label>
              <label className="block">
                <span className="text-[12px] text-[#555]">Hora</span>
                <input
                  type="time"
                  value={formData.hora_cita_propuesta}
                  onChange={(e) => setFormData((prev) => ({ ...prev, hora_cita_propuesta: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-[#ececec] bg-[#f8f9fa] px-3 py-2 text-[13px]"
                />
              </label>
            </div>

            {error ? <p className="text-[12px] text-[#e05555]">{error}</p> : null}

            <button
              type="submit"
              disabled={creating}
              className="w-full h-11 rounded-xl bg-[#7a9a52] text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <PawPrint className="w-4 h-4" />
              {creating ? 'Enviando...' : 'Enviar solicitud'}
            </button>
          </form>
        </div>
      )}
    </div>
  )
}
